
import { Component, Prop, Vue } from "vue-property-decorator";
import { SMUser, SMTasks, SMCases } from "@/store";
import { EMPLOYEE_ROLE, EMPLOYER_ROLE } from "@/constants/roles";
import {
  MY_TASKS_TAB,
  EMPLOYER_TABS,
  EMPLOYEE_TABS,
  EMPLOYEE_TASKS_TABS,
  EMPLOYER_EMPLOYEES_TASKS_TABS,
  TASK_STATUS_LABELS,
  EXPERT_EMPLOYERS_AND_EMPLOYEES_TASK_TABS,
  EXPERT_EMPLOYERS_AND_EMPLOYEES_TABS,
} from "@/constants/tasks";
import { TTask_Table, TActions } from "@/models/tasks";

import { RCandidateServices } from "@/modules/Services/RServices";

const CASE_PRODUCT_TYPE = "case";

const VALUE_NAME = "name";
const VALUE_STATUS = "status";
const VALUE_SECTION = "complianceSection";
const VALUE_DUE_DATE = "dueDate";

Component.registerHooks(["beforeRouteEnter"]);

@Component({
  components: {
    Loader: () => import("@/components/Loader/Loader.vue"),
    TableTabs: () => import("@/modules/Tasks/Table/TableTabs.vue"),
    ComplianceExpiredWidget: () => import("@/modules/Compliance/ComplianceExpiredWidget.vue"),
  },
})
export default class EmployeeSummaryTasksTable extends Vue {
  @Prop({ type: Array, required: true }) readonly tasksEmployee!: TTask_Table[];
  @Prop({ type: Array, required: true }) readonly tasksEmployer!: TTask_Table[];

  get employeeTasks() {
    return this.tasksEmployee.map((item) => {
      const { status } = item;

      Object.assign(item, {
        status: TASK_STATUS_LABELS[status] ?? null,
      });

      return item;
    });
  }

  get employerTasks() {
    return this.tasksEmployer.map((item) => {
      const { status } = item;

      Object.assign(item, {
        status: TASK_STATUS_LABELS[status] ?? null,
      });

      return item;
    });
  }

  defaultActions: TActions = {
    delete: this.deleteTask,
  };

  actions: TActions = {};

  get availableActions(): TActions {
    if (SMUser.isExpert) {
      return this.defaultActions;
    }

    const hide = (SMUser.isEmployee || SMUser.isEmployer) && !SMCases.caseId;

    if (hide) return {};

    return this.defaultActions;
  }

  get isComplianceBlocked() {
    return SMUser.user?.is_compliance_blocked;
  }

  get loading() {
    return SMTasks.loading;
  }

  get companyId(): number {
    return Number(this.$route.params.company_id ?? 0);
  }

  async getTableTasks() {
    const role = SMUser.isEmployer ? EMPLOYER_ROLE : EMPLOYEE_ROLE;
    const sideRole = SMUser.isEmployer ? EMPLOYEE_ROLE : EMPLOYER_ROLE;

    await SMTasks.getMyTasks({ role: role });
    await SMTasks.getSideTasks({ role: sideRole });
  }

  async beforeRouteEnter(to: any, from: any, next: any) {
    next(async (vm: any) => {
      if (vm.companyId) SMTasks.setCompanyId(vm.companyId);

      await vm.$nextTick();

      await vm.getTableTasks();
    });
  }

  mounted() {
    this.$root.$on("update-company-level-data", this.getTableTasks);
  }

  get tabValues() {
    if (SMUser.isExpert) return EXPERT_EMPLOYERS_AND_EMPLOYEES_TABS;

    return SMUser.isEmployer ? EMPLOYER_TABS : EMPLOYEE_TABS;
  }

  get tabNames() {
    if (SMUser.isExpert) return EXPERT_EMPLOYERS_AND_EMPLOYEES_TASK_TABS;

    return SMUser.isEmployer ? EMPLOYER_EMPLOYEES_TASKS_TABS : EMPLOYEE_TASKS_TABS;
  }

  navigateTask(item: TTask_Table) {
    const { id } = item;

    let path = `${SMUser.basePath}/tasks/${id}`;

    if (item?.productType === CASE_PRODUCT_TYPE) {
      path = `/${RCandidateServices.path}/tasks/${id}`;
    }

    if (this.companyId) path = `/expert/companies/${this.companyId}/tasks/${id}`;

    this.$router.push(path);
  }

  async deleteTask(id: number) {
    await SMTasks.archiveTableTask(id);

    await this.$nextTick();

    this.$root.$emit("update-employee-level-data");
  }

  tabName = "";

  setTabName(value: string) {
    this.tabName = value;
  }

  get tableHeaders() {
    return [
      {
        text: this.$t("table.headers.name"),
        value: VALUE_NAME,
      },
      {
        text: this.$t("table.headers.status"),
        value: VALUE_STATUS,
        slot: true,
        filterable: true,
        multipleSelect: true,
        filterSearch: false,
      },
      {
        text: this.$t("table.headers.complianceSection"),
        value: VALUE_SECTION,
      },
      {
        text: this.$t("table.headers.dueDate"),
        value: VALUE_DUE_DATE,
        hint: this.$tc("table.hints.dueDate"),
      },
    ];
  }

  get tableWithActionByColumnHeaders() {
    return [
      {
        text: this.$t("table.headers.name"),
        value: VALUE_NAME,
      },
      {
        text: this.$t("table.headers.status"),
        value: VALUE_STATUS,
        slot: true,
        filterable: true,
        multipleSelect: true,
        filterSearch: false,
      },
      {
        text: this.$t("table.headers.complianceSection"),
        value: VALUE_SECTION,
      },
      {
        text: this.$t("table.headers.dueDate"),
        value: VALUE_DUE_DATE,
        hint: this.$tc("table.hints.dueDate"),
      },
    ];
  }

  get headerValues() {
    if (this.tabName === MY_TASKS_TAB) {
      return this.tableHeaders;
    } else {
      return SMUser.isEmployer ? this.tableWithActionByColumnHeaders : this.tableHeaders;
    }
  }
}
